/**
 * Building blocks for the modern dashboards (Executive + Consultant home).
 * Pure presentation — every number comes from the caller, nothing is invented.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './modern.css';
import { Icon } from './icons';
import { fmtInt, pct } from './format';
import HeroCarousel from './HeroCarousel';

/* ---------------------------------------------------------------- bits */
export function Delta({ value, suffix = 'vs prev.' }) {
  if (value == null) return null;
  const cls = value > 0 ? 'up' : value < 0 ? 'down' : 'flat';
  return (
    <span className={`md-delta ${cls}`} title={`${value > 0 ? '+' : ''}${value}% ${suffix}`}>
      {value > 0 ? <span style={{ width: 11, height: 11, display: 'inline-flex' }}>{Icon.arrowUp}</span>
        : value < 0 ? <span style={{ width: 11, height: 11, display: 'inline-flex' }}>{Icon.arrowDown}</span> : null}
      {Math.abs(value)}%
    </span>
  );
}

/** Smooth area sparkline. */
export function Spark({ values = [], color = 'var(--accent-primary)', w = 110, h = 30 }) {
  if (!values || values.length < 2) return null;
  const max = Math.max(1, ...values);
  const step = w / (values.length - 1);
  const pts = values.map((v, i) => [i * step, h - 3 - (v / max) * (h - 6)]);
  const line = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const id = `sp${Math.abs(hash(color + values.join(',')))}`;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden style={{ display: 'block' }}>
      <defs>
        <linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor={color} stopOpacity="0.35" />
          <stop offset="1" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${line} L${w},${h} L0,${h} Z`} fill={`url(#${id})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function hash(s) { let h = 0; for (let i = 0; i < s.length; i += 1) h = ((h << 5) - h + s.charCodeAt(i)) | 0; return h; }

const AVATAR_COLORS = ['#7c3aed', '#2563eb', '#0891b2', '#059669', '#d97706', '#db2777', '#4f46e5', '#0d9488'];
export function Avatar({ name, size = 28 }) {
  const initials = String(name || '?').trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';
  const bg = AVATAR_COLORS[Math.abs(hash(String(name || ''))) % AVATAR_COLORS.length];
  return <span className="md-avatar" style={{ width: size, height: size, background: bg }}>{initials}</span>;
}

/* ---------------------------------------------------------------- hero */
export function Hero({ eyebrow, title, highlight, sub, chips = [], slides = [] }) {
  return (
    <section className="md-hero md-rise">
      <div className="md-hero-grid">
        <div>
          <div className="md-eyebrow">{eyebrow}</div>
          <h1>{title} {highlight && <span className="md-grad">{highlight}</span>}</h1>
          {sub && <div className="md-hero-sub">{sub}</div>}
          {chips.length > 0 && (
            <div className="md-hero-chips">
              {chips.map((c) => {
                const inner = (<><span className="md-chip-dot" /><b>{c.value}</b> {c.label}</>);
                return c.to
                  ? <Link key={c.key} to={c.to} className={`md-chip${c.alert ? ' alert' : ''}`}>{inner}</Link>
                  : <span key={c.key} className={`md-chip${c.alert ? ' alert' : ''}`}>{inner}</span>;
              })}
            </div>
          )}
        </div>
        <div className="md-hero-car">
          <HeroCarousel slides={slides} />
        </div>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- stat */
export function StatCard({ label, value, icon, tone = 'var(--accent-primary)', delta, sub, spark, to, index = 0 }) {
  const body = (
    <>
      <div className="md-stat-top">
        <span className="md-icon">{icon}</span>
        <span className="md-stat-label">{label}</span>
      </div>
      <div className="md-stat-value">{value}</div>
      <div className="md-stat-foot">
        {delta != null ? <Delta value={delta} /> : <span className="md-stat-sub">{sub}</span>}
        {spark && <Spark values={spark} color={tone} w={84} h={24} />}
      </div>
      {delta != null && sub && <div className="md-stat-sub">{sub}</div>}
    </>
  );
  const style = { '--md-tone': tone, animationDelay: `${index * 50}ms` };
  return to
    ? <Link to={to} className="md-stat md-rise" style={style}>{body}</Link>
    : <div className="md-stat md-rise" style={style}>{body}</div>;
}

/* ---------------------------------------------------------------- panel */
export function Panel({ title, sub, icon, tone, link, linkLabel = 'View all', children, style, className = '' }) {
  return (
    <section className={`md-panel md-rise ${className}`} style={style}>
      <div className="md-panel-head">
        {icon && <span className="md-icon" style={tone ? { '--md-tone': tone } : undefined}>{icon}</span>}
        <div>
          <div className="md-panel-title">{title}</div>
          {sub && <div className="md-panel-sub">{sub}</div>}
        </div>
        {link && <Link to={link} className="md-panel-link">{linkLabel} →</Link>}
      </div>
      <div className="md-panel-body">{children}</div>
    </section>
  );
}

/* ---------------------------------------------------------------- donut */
export function Donut({ segments = [], size = 150, thickness = 20, centerValue, centerLabel }) {
  const total = segments.reduce((a, s) => a + (s.value || 0), 0);
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={centerLabel}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--bg-glass)" strokeWidth={thickness} />
      {total > 0 && segments.filter((s) => s.value > 0).map((s) => {
        const len = (s.value / total) * c;
        const el = (
          <circle key={s.label} cx={size / 2} cy={size / 2} r={r} fill="none"
            stroke={s.color} strokeWidth={thickness}
            strokeDasharray={`${Math.max(0, len - 2)} ${c}`} strokeDashoffset={-offset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`} strokeLinecap="butt" />
        );
        offset += len;
        return el;
      })}
      <text x="50%" y="48%" textAnchor="middle" fontSize="24" fontWeight="800" fill="var(--text-primary)">{centerValue ?? fmtInt(total)}</text>
      <text x="50%" y="62%" textAnchor="middle" fontSize="11" fill="var(--text-tertiary)">{centerLabel}</text>
    </svg>
  );
}
export function DonutLegend({ segments = [], centerLabel, size = 150 }) {
  const total = segments.reduce((a, s) => a + (s.value || 0), 0);
  if (!total) return <div className="md-empty">Nothing in this period yet.</div>;
  return (
    <div className="md-donut-wrap">
      <Donut segments={segments} size={size} centerLabel={centerLabel} />
      <div className="md-legend">
        {segments.filter((s) => s.value > 0).map((s) => (
          <div key={s.label} className="md-legend-row">
            <span className="md-legend-dot" style={{ background: s.color }} />
            <span className="md-legend-name">{s.label}</span>
            <span className="md-legend-val">{fmtInt(s.value)}</span>
            <span className="md-legend-pct">{pct(s.value, total)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- trend */
/** Dual-series daily trend. series: [{ label, color, values }], labels: x-axis day labels. */
export function TrendChart({ series = [], labels = [], height = 190 }) {
  // Draw at the container's real pixel width — a stretched viewBox
  // (preserveAspectRatio="none") squashed the axis text.
  const wrapRef = useRef(null);
  const [W, setW] = useState(640);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => {
      const w = Math.round(entry.contentRect.width);
      if (w > 0) setW(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const H = height; const L = 30; const B = 22; const T = 10; const R = 10;
  const n = Math.max(0, ...series.map((s) => s.values.length));
  if (n < 2) return <div className="md-empty">Not enough days to draw a trend yet.</div>;
  const max = Math.max(4, ...series.flatMap((s) => s.values));
  const niceMax = Math.ceil(max / 4) * 4;
  const x = (i) => L + (i * (W - L - R)) / (n - 1);
  const y = (v) => T + (H - T - B) * (1 - v / niceMax);
  const ticks = [0, 1, 2, 3, 4].map((k) => (niceMax * k) / 4);
  const labelEvery = Math.max(1, Math.ceil(n / 6));
  return (
    <div ref={wrapRef} className="md-trend-wrap">
    <svg className="md-trend-chart" width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Daily trend">
      <defs>
        {series.map((s, si) => (
          <linearGradient key={si} id={`tr${si}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor={s.color} stopOpacity="0.28" />
            <stop offset="1" stopColor={s.color} stopOpacity="0" />
          </linearGradient>
        ))}
      </defs>
      {ticks.map((t) => (
        <g key={t}>
          <line x1={L} x2={W - R} y1={y(t)} y2={y(t)} stroke="var(--border-subtle)" />
          <text x={L - 6} y={y(t) + 3} textAnchor="end" fontSize="10" fill="var(--text-tertiary)">{Math.round(t)}</text>
        </g>
      ))}
      {labels.map((lb, i) => (i % labelEvery === 0 || i === n - 1) && (
        <text key={i} x={x(i)} y={H - 6} textAnchor="middle" fontSize="10" fill="var(--text-tertiary)">{lb}</text>
      ))}
      {series.filter((s) => s.type === 'bar').map((s) => {
        const bw = Math.max(4, Math.min(18, ((W - L - R) / n) * 0.45));
        return (
          <g key={s.label}>
            {s.values.map((v, i) => v > 0 && (
              <rect key={i} x={x(i) - bw / 2} y={y(v)} width={bw} height={Math.max(0, H - B - y(v))}
                rx={Math.min(4, bw / 2)} fill={s.color} opacity="0.85">
                <title>{`${labels[i] || ''}: ${v} ${s.label.toLowerCase()}`}</title>
              </rect>
            ))}
          </g>
        );
      })}
      {series.map((s, si) => {
        if (s.type === 'bar') return null;
        const line = s.values.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
        return (
          <g key={si}>
            <path d={`${line} L${x(s.values.length - 1)},${H - B} L${x(0)},${H - B} Z`} fill={`url(#tr${si})`} />
            <path d={line} fill="none" stroke={s.color} strokeWidth="2.4" strokeLinejoin="round" strokeLinecap="round" />
            {s.values.map((v, i) => (i === s.values.length - 1) && (
              <circle key={i} cx={x(i)} cy={y(v)} r="4" fill={s.color} stroke="var(--bg-secondary)" strokeWidth="2" />
            ))}
          </g>
        );
      })}
    </svg>
    </div>
  );
}

/* ---------------------------------------------------------------- ranked */
export function RankList({ rows = [], unit = '' }) {
  if (!rows.length) return <div className="md-empty">Nothing to rank in this period yet.</div>;
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="md-rank">
      {rows.map((r, i) => (
        <div key={r.key} className="md-rank-row">
          <span className="md-rank-n">{i + 1}</span>
          {r.photo ? <img className="md-rank-img" src={r.photo} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} /> : <span />}
          <div style={{ minWidth: 0 }}>
            <div className="md-rank-name">{r.label}</div>
            <div className="md-rank-bar"><div style={{ width: `${Math.round((r.value / max) * 100)}%` }} /></div>
          </div>
          <span className="md-rank-val">{fmtInt(r.value)}{unit}</span>
        </div>
      ))}
    </div>
  );
}

export function MiniMetric({ icon, value, label, tone, to }) {
  const inner = (
    <>
      <span className="md-mini-icon" style={tone ? { '--md-tone': tone, color: tone } : undefined}>{icon}</span>
      <span className="md-mini-value">{value}</span>
      <span className="md-mini-label">{label}</span>
    </>
  );
  return to ? <Link to={to} className="md-mini">{inner}</Link> : <div className="md-mini">{inner}</div>;
}
